import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { inferRouterOutputs } from '@trpc/server';

import AdminLayout from '@/components/Layout/AdminLayout';
import Alert from '@/components/UI/Alert';
import { Button, Card, ConfirmationDialog, DataTable } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { AppRouter } from '@/types/appRouter';
import { getSupportWriteScopeLabel } from '@/utils/supportWriteScopes';
import StartSupportSessionModal from '@/components/Support/StartSupportSessionModal';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type SupportSessionReview = RouterOutputs['support']['listRecentSessions'][number];
type SupportSessionAuditEvent = RouterOutputs['support']['getSessionAudit']['events'][number];

const formatSupportDateTime = (value: string | null | undefined) => {
    if (!value) {
        return '—';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const statusClasses: Record<SupportSessionReview['status'], string> = {
    active: 'bg-emerald-900/40 text-emerald-300 border border-emerald-700',
    ended: 'bg-zinc-800 text-zinc-300 border border-zinc-700',
    revoked: 'bg-red-900/40 text-red-300 border border-red-700'
};

const outcomeClasses: Record<SupportSessionAuditEvent['outcome'], string> = {
    success: 'bg-emerald-900/40 text-emerald-300 border border-emerald-700',
    denied: 'bg-amber-900/40 text-amber-300 border border-amber-700',
    error: 'bg-red-900/40 text-red-300 border border-red-700'
};

function formatMetadataJson(metadataJson: string | null): string {
    if (!metadataJson) {
        return '';
    }

    try {
        return JSON.stringify(JSON.parse(metadataJson), null, 2);
    } catch {
        return metadataJson;
    }
}

const SupportSessions = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isStartModalOpen, setIsStartModalOpen] = useState(false);
    const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
    const queryClient = useQueryClient();
    const selectedSessionId = Number(searchParams.get('sessionId') || '0');
    const shouldAutoOpenStartModal = searchParams.get('startSession') === '1';

    const updateSearchParams = (updates: Record<string, string | null>, options?: { replace?: boolean }) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        for (const [key, value] of Object.entries(updates)) {
            if (value === null) {
                nextSearchParams.delete(key);
            } else {
                nextSearchParams.set(key, value);
            }
        }

        setSearchParams(nextSearchParams, options);
    };

    const openStartModal = () => {
        setIsStartModalOpen(true);
    };

    const closeStartModal = () => {
        setIsStartModalOpen(false);

        if (shouldAutoOpenStartModal) {
            updateSearchParams({ startSession: null }, { replace: true });
        }
    };

    useEffect(() => {
        if (shouldAutoOpenStartModal) {
            setIsStartModalOpen(true);
        }
    }, [shouldAutoOpenStartModal]);

    const sessionsQuery = useAppQuery(
        {
            ...trpc.support.listRecentSessions.queryOptions()
        },
        {
            fallbackErrorMessage: 'Failed to load support sessions',
            showErrorToast: true
        }
    );

    const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data]);

    useEffect(() => {
        if (sessions.length === 0) {
            return;
        }

        if (!selectedSessionId || !sessions.some((session) => session.id === selectedSessionId)) {
            updateSearchParams({ sessionId: String(sessions[0].id) }, { replace: true });
        }
    }, [selectedSessionId, sessions]);

    const selectedSession = useMemo(
        () => sessions.find((session) => session.id === selectedSessionId) ?? null,
        [selectedSessionId, sessions]
    );

    const auditQuery = useAppQuery(
        {
            ...trpc.support.getSessionAudit.queryOptions({
                supportSessionId: selectedSessionId
            }),
            enabled: selectedSessionId > 0
        },
        {
            fallbackErrorMessage: 'Failed to load support session audit details',
            showErrorToast: true
        }
    );

    const auditSession = auditQuery.data?.session ?? null;
    const auditEvents = auditQuery.data?.events ?? [];
    const deniedEventCount = auditEvents.filter((event) => event.outcome === 'denied').length;
    const errorEventCount = auditEvents.filter((event) => event.outcome === 'error').length;

    const revokeSessionMutation = useTrpcMutation(
        trpc.support.revokeSession.mutationOptions({
            onSuccess: async (_data, variables) => {
                setIsRevokeDialogOpen(false);

                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: trpc.support.listRecentSessions.queryOptions().queryKey
                    }),
                    queryClient.invalidateQueries({
                        queryKey: trpc.support.getSessionAudit.queryOptions({
                            supportSessionId: variables.supportSessionId
                        }).queryKey
                    })
                ]);
            }
        }),
        {
            successMessage: 'Support session revoked.',
            fallbackErrorMessage: 'Failed to revoke support session'
        }
    );

    const columns: ColumnDef<SupportSessionReview>[] = useMemo(
        () => [
            {
                accessorKey: 'status',
                header: 'Status',
                size: 100,
                cell: ({ row }) => (
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${statusClasses[row.original.status]}`}>
                        {row.original.status}
                    </span>
                )
            },
            {
                accessorKey: 'effectiveUserEmail',
                header: 'Target User',
                size: 240,
                cell: ({ row }) => (
                    <div>
                        <div className="text-zinc-100">{row.original.effectiveUserEmail}</div>
                        <div className="text-xs text-zinc-500">User #{row.original.effectiveUserId}</div>
                    </div>
                )
            },
            {
                accessorKey: 'mode',
                header: 'Mode',
                size: 120
            },
            {
                accessorKey: 'stepUpMethod',
                header: 'Step-Up',
                size: 110,
                cell: ({ row }) => (row.original.stepUpMethod === 'passkey' ? 'Passkey' : 'TOTP')
            },
            {
                accessorKey: 'allowedWriteScopes',
                header: 'Write Scopes',
                size: 220,
                cell: ({ row }) =>
                    row.original.allowedWriteScopes.length > 0 ? (
                        <div className="text-xs text-zinc-300">
                            {row.original.allowedWriteScopes.map(getSupportWriteScopeLabel).join(', ')}
                        </div>
                    ) : (
                        <span className="text-xs text-zinc-500">Read-only</span>
                    )
            },
            {
                accessorKey: 'ticketReference',
                header: 'Ticket',
                size: 130,
                cell: ({ row }) => row.original.ticketReference ?? '—'
            },
            {
                accessorKey: 'createdDateTime',
                header: 'Started',
                size: 170,
                cell: ({ row }) => formatSupportDateTime(row.original.createdDateTime)
            },
            {
                accessorKey: 'lastSeenDateTime',
                header: 'Last Seen',
                size: 170,
                cell: ({ row }) => formatSupportDateTime(row.original.lastSeenDateTime)
            }
        ],
        []
    );

    if (sessionsQuery.isLoading) {
        return (
            <AdminLayout containerSize="2200px" title="Support Sessions">
                <DataLoadingSpinner text="Loading support sessions..." />
            </AdminLayout>
        );
    }

    if (sessions.length === 0) {
        return (
            <AdminLayout containerSize="2200px" title="Support Sessions">
                <div className="container mx-auto px-4">
                    <div className="mb-4 flex justify-end">
                        <Button size="sm" onClick={openStartModal}>
                            Start Support Session
                        </Button>
                    </div>
                    <Alert variant="info">No support sessions have been recorded for this admin account yet.</Alert>
                </div>
                <StartSupportSessionModal isOpen={isStartModalOpen} onClose={closeStartModal} />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout containerSize="2200px" title="Support Sessions">
            <div className="container mx-auto px-4 space-y-6">
                <div className="mt-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h3 className="text-white text-xl font-semibold">Support Sessions</h3>
                            <p className="mt-2 text-sm text-zinc-400">
                                Review recent support sessions, granted limited-write scopes, and the audit trail of allowed, denied, and
                                failed support actions.
                            </p>
                        </div>
                        <Button size="sm" onClick={openStartModal}>
                            Start Support Session
                        </Button>
                    </div>
                </div>

                <Card title="Recent Sessions">
                    <DataTable
                        allowOverflow
                        search
                        sort
                        striped
                        columns={columns}
                        data={sessions}
                        pagination={{ enabled: true, pageSize: 20 }}
                        onRowClick={(row) => updateSearchParams({ sessionId: String(row.id) })}
                    />
                </Card>

                {selectedSession && (
                    <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
                        <Card
                            headerActions={
                                auditSession?.status === 'active' ? (
                                    <Button
                                        disabled={revokeSessionMutation.isPending}
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => setIsRevokeDialogOpen(true)}
                                    >
                                        Revoke Session
                                    </Button>
                                ) : undefined
                            }
                            title="Session Detail"
                        >
                            {auditQuery.isLoading || !auditSession ? (
                                <DataLoadingSpinner text="Loading session audit..." />
                            ) : (
                                <div className="space-y-4 text-sm">
                                    <div>
                                        <div className="text-zinc-500">Status</div>
                                        <div className="mt-1">
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium uppercase ${statusClasses[auditSession.status]}`}
                                            >
                                                {auditSession.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Actor</div>
                                        <div className="mt-1 text-zinc-100">{auditSession.actorUserEmail}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Target User</div>
                                        <div className="mt-1 text-zinc-100">{auditSession.effectiveUserEmail}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Mode</div>
                                        <div className="mt-1 text-zinc-100">{auditSession.mode}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Step-Up Method</div>
                                        <div className="mt-1 text-zinc-100">
                                            {auditSession.stepUpMethod === 'passkey' ? 'Passkey' : 'TOTP'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Allowed Write Scopes</div>
                                        <div className="mt-1 text-zinc-100">
                                            {auditSession.allowedWriteScopes.length > 0
                                                ? auditSession.allowedWriteScopes.map(getSupportWriteScopeLabel).join(', ')
                                                : 'Read-only'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Ticket Reference</div>
                                        <div className="mt-1 text-zinc-100">{auditSession.ticketReference ?? '—'}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Reason</div>
                                        <div className="mt-1 whitespace-pre-wrap text-zinc-100">{auditSession.reason}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Started</div>
                                        <div className="mt-1 text-zinc-100">{formatSupportDateTime(auditSession.createdDateTime)}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Last Seen</div>
                                        <div className="mt-1 text-zinc-100">{formatSupportDateTime(auditSession.lastSeenDateTime)}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Session Expiry</div>
                                        <div className="mt-1 text-zinc-100">{formatSupportDateTime(auditSession.expiresDateTime)}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Hard Expiry</div>
                                        <div className="mt-1 text-zinc-100">
                                            {formatSupportDateTime(auditSession.absoluteExpiresDateTime)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Ended</div>
                                        <div className="mt-1 text-zinc-100">{formatSupportDateTime(auditSession.endedDateTime)}</div>
                                    </div>
                                    <div>
                                        <div className="text-zinc-500">Revoked</div>
                                        <div className="mt-1 text-zinc-100">{formatSupportDateTime(auditSession.revokedDateTime)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card
                            subtitle={`${auditEvents.length} events • ${deniedEventCount} denied • ${errorEventCount} errors`}
                            title="Audit Events"
                        >
                            {auditQuery.isLoading ? (
                                <DataLoadingSpinner text="Loading audit events..." />
                            ) : auditEvents.length === 0 ? (
                                <div className="text-sm text-zinc-500">No audit events recorded for this session.</div>
                            ) : (
                                <div className="space-y-3">
                                    {auditEvents.map((event) => (
                                        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" key={event.id}>
                                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span
                                                            className={`px-2 py-1 rounded text-xs font-medium uppercase ${outcomeClasses[event.outcome]}`}
                                                        >
                                                            {event.outcome}
                                                        </span>
                                                        <span className="font-medium text-zinc-100">{event.eventType}</span>
                                                    </div>
                                                    <div className="mt-2 text-xs text-zinc-500">
                                                        {formatSupportDateTime(event.createdDateTime)}
                                                        {event.requestMethod ? ` • ${event.requestMethod}` : ''}
                                                        {event.procedurePath ? ` • ${event.procedurePath}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            {event.metadataJson && (
                                                <pre className="mt-3 overflow-x-auto rounded bg-[#111111] p-3 text-xs text-zinc-300 whitespace-pre-wrap">
                                                    {formatMetadataJson(event.metadataJson)}
                                                </pre>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </div>

            <ConfirmationDialog
                confirmText="Revoke Session"
                confirmVariant="danger"
                isConfirmLoading={revokeSessionMutation.isPending}
                isOpen={isRevokeDialogOpen}
                message={
                    auditSession
                        ? `Revoke support session #${auditSession.id.toString()} for ${auditSession.effectiveUserEmail}? The session will be terminated immediately and any remaining support cookie will stop working.`
                        : 'Revoke this support session?'
                }
                title="Revoke Support Session"
                onCancel={() => setIsRevokeDialogOpen(false)}
                onConfirm={() => {
                    if (!auditSession) {
                        return;
                    }

                    revokeSessionMutation.mutate({
                        supportSessionId: auditSession.id
                    });
                }}
            />
            <StartSupportSessionModal isOpen={isStartModalOpen} onClose={closeStartModal} />
        </AdminLayout>
    );
};

export default SupportSessions;
